import {
    mdiAccount,
    mdiAccountTie,
    mdiCodeBraces,
    mdiCreation,
    mdiMinusCircleOutline,
    mdiArrowRightBold,
    mdiCheckDecagram,
    mdiClipboardPulseOutline,
    mdiClipboardTextOutline,
    mdiFileDocumentOutline,
    mdiHistory,
    mdiLock,
    mdiPlaylistRemove,
    mdiReceipt,
    mdiShieldOffOutline,
    mdiSwapHorizontal,
    mdiFileChart,
    mdiTrendingDown,
} from '@mdi/js';
import {
    RuleActionType,
    AUTOMATION_WORKER_ICONS,
    AutomationWorker,
    AutomationWorkerId,
} from './worker-automation.types';

export interface AutomationWorkerDefinition extends Omit<AutomationWorker, 'rules'> {}

export const AUTOMATION_WORKER_DEFINITIONS: Record<AutomationWorkerId, AutomationWorkerDefinition> = {
    rejection: {
        id: 'rejection',
        name: 'Provider Rejection Worker',
        key: 'PROVIDER_REJECTION_WORKER',
        icon: AUTOMATION_WORKER_ICONS.rejection,
        scope: 'TRANSMISSION_STATUS',
        scopeLabel: 'Pre-adjudication · transmission failures',
        desc: 'Routes pre-adjudication transmission or clearinghouse failures into the right rejection follow-up path.',
    },
    denial: {
        id: 'denial',
        name: 'Provider Denial Worker',
        key: 'PROVIDER_DENIAL_WORKER',
        icon: AUTOMATION_WORKER_ICONS.denial,
        scope: 'PAYMENT',
        scopeLabel: 'Post-adjudication · ERA denial codes',
        desc: 'Routes post-adjudication ERA denial codes into configured denial remediation workflows.',
    },
};

export interface ActionSummaryHeader {
    icon: string;
    title: string;
}

export interface ActionOutcomePill {
    icon: string;
    label: string;
}

export const ACTION_SUMMARY_HEADERS: Record<RuleActionType, ActionSummaryHeader> = {
    CLOSE_OUT: {
        icon: mdiMinusCircleOutline,
        title: 'ENTER closes out the claim',
    },
    SWITCH_TO_PRIVATE_PAY: {
        icon: mdiSwapHorizontal,
        title: 'ENTER converts the claim to self-pay',
    },
    CREATE_APPEAL: {
        icon: mdiCreation,
        title: 'ENTER generates the full reconsideration packet',
    },
    CREATE_MESSAGE: {
        icon: mdiAccountTie,
        title: 'ENTER creates a task for humans to review',
    },
    EXECUTE_ENTER_FUNCTION: {
        icon: mdiCodeBraces,
        title: 'ENTER runs your function against the claim',
    },
};

export const ACTION_FIRES_HELPERS: Record<RuleActionType, string> = {
    CLOSE_OUT:
        'When this rule fires, ENTER closes the claim and stops working it. In reporting, the claim is counted as a write-off.',
    SWITCH_TO_PRIVATE_PAY:
        'When this rule fires, ENTER flips the responsibility on the claim from insurance to a self-pay patient and routes it to guarantor collections — an invoice is generated and sent to the patient.',
    CREATE_APPEAL:
        'When this rule fires, ENTER automatically assembles and submits a complete reconsideration packet to the payer. No action is required — the fields below only let you tailor it.',
    CREATE_MESSAGE:
        'When this rule fires, ENTER opens a task for a person to review the claim. Use the responsibility owner below to decide who works it.',
    EXECUTE_ENTER_FUNCTION:
        'When this rule fires, ENTER runs your function against the claim payload before any other processing continues. Any coder rules you disable below are skipped for this claim only.',
};

/** @deprecated Use ACTION_FIRES_HELPERS */
export const ACTION_HELPERS = ACTION_FIRES_HELPERS;

export const ACTION_OUTCOME_PILLS: Record<RuleActionType, ActionOutcomePill[]> = {
    CLOSE_OUT: [
        { icon: mdiLock, label: 'Status → Closed' },
        { icon: mdiTrendingDown, label: 'Counted as write-off' },
        { icon: mdiHistory, label: 'Audit event logged' },
    ],
    SWITCH_TO_PRIVATE_PAY: [
        { icon: mdiAccount, label: 'Responsibility → Patient' },
        { icon: mdiReceipt, label: 'Invoice generated' },
        { icon: mdiArrowRightBold, label: 'Sent to guarantor collections' },
    ],
    CREATE_APPEAL: [
        { icon: mdiFileDocumentOutline, label: 'AI cover letter' },
        { icon: mdiClipboardTextOutline, label: 'Payer reconsideration form' },
        { icon: mdiShieldOffOutline, label: 'Waiver of responsibility' },
        { icon: mdiReceipt, label: 'CMS 1500' },
        { icon: mdiFileChart, label: 'EOB' },
        { icon: mdiCheckDecagram, label: 'Proof of transmission' },
        { icon: mdiClipboardPulseOutline, label: 'All medical records' },
    ],
    CREATE_MESSAGE: [],
    EXECUTE_ENTER_FUNCTION: [
        { icon: mdiCodeBraces, label: 'Claim payload edited by your function' },
        { icon: mdiPlaylistRemove, label: 'Selected coder rules skipped' },
    ],
};

export const RULE_FIELD_HELPERS = {
    closeoutDescription: 'A brief label for why the claim was closed — shown in reporting and the claim’s audit trail.',
    memo: 'Attached to the invoice sent to the patient to explain why they’re receiving it.',
    prompt: 'Prompt our AI with any specific arguments or requirements for this reconsideration — it adjusts how the cover letter is written.',
    documents:
        'Attach anything you want included on top of the packet above (e.g. medical records, prior auth letters).',
    messageOwner: 'Who is responsible for reviewing this task.',
    messageCategory:
        'Select an existing task category, or click “+ Create new category” beside this dropdown to add your own. After you create it, the dropdown will include and select that category.',
    assigned: 'People to assign this task to.',
    suggestedResolution: 'Dropped into the task’s first comment to guide whoever reviews it.',
    group: 'Reuse a single task for claims that match this rule. The rule name is the grouping key.',
    ruleName:
        'Must be unique across denial and rejection rules (case-insensitive). When grouping is enabled for this rule, this name is the grouping key.',
    function:
        'Runs with `payload` (the claim) as its only argument. Return the edited payload. Throw an Error to stop processing instead.',
    disableCodingRules:
        'Every coder rule runs by default. Uncheck a rule to skip it for claims this rule fires on.',
} as const;
